import { useCallback, useMemo, useState } from 'react';

import useSideDetail from '#/hooks/useSideDetail';
import SideMsg from '#/modules/commonComponents/SideMsg';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import PageShell from '#/modules/infra/layout/PageShell';
import {
  applyTotalAmountRange,
  TOTAL_AMOUNT_FILTER_STATUS_FIELDS,
} from '#/modules/finMgmt/shared/helper/fmsListRangeFilters';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';

import {
  buildPaymentRequestColumns,
  paymentRequestSideMsgBlocks,
} from '../data/paymentRequestColumns';
import { getPaymentRequestApiErrorMessage } from '../helper/apiError';
import { PAYMENT_REQUEST_LIST_SORT_FIELD_LABELS } from '../helper/paymentRequestStatus';
import usePaymentRequestList from '../hooks/usePaymentRequestList';

/**
 * 財務管理：請款單列表（會計審核用，看全部領款人）。
 * 新增請款在個人中心；這裡不放新增鈕。
 */
const PaymentRequestListPage = () => {
  const { displayData, setSelectedData } = useSideDetail();
  const [payeeLabelCache] = useState(createAsyncFilterLabelCache);
  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
  } = usePaymentRequestList();

  const columns = useMemo(
    () => buildPaymentRequestColumns({
      filters: listQuery.filters || {},
      onFilterChange: setFilter,
      mode: 'finance',
      payeeLabelCache,
      onTotalAmountRangeChange: (range) => applyTotalAmountRange(setFilter, range),
    }),
    [listQuery.filters, setFilter, payeeLabelCache],
  );

  const filterStatusFields = useMemo(
    () => [
      ...buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
      ...TOTAL_AMOUNT_FILTER_STATUS_FIELDS,
    ],
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    setSelectedData(isSelected ? row.original : null);
  }, [setSelectedData]);

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              tableKey="fms.paymentRequest.finance.list"
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={
                isError
                  ? getPaymentRequestApiErrorMessage(error, '載入請款單失敗')
                  : undefined
              }
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋單號、事由或備註"
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={PAYMENT_REQUEST_LIST_SORT_FIELD_LABELS}
              onFilterChange={setFilter}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
            />
          </div>
          <SideMsg
            title="請款單"
            titleValue="requestNo"
            displayData={displayData}
            blocks={paymentRequestSideMsgBlocks}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default PaymentRequestListPage;
